/**
 * CommentCard — Single comment display in the margin zone.
 *
 * Shows author avatar, name, relative time, body, and overflow menu
 * (edit/copy/delete). Edit mode inline with 15-minute window.
 *
 * See: docs/features/document-system/document-comments.md
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import { MoreHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import { meetsMinRole } from "../../utils/roleUtils";

// ---------- helpers ----------

const BODY_TRUNCATE_LENGTH = 150;

function formatTimeAgo(timestamp) {
  if (!timestamp) return "";
  const ms = Date.now() - new Date(timestamp?.toDate?.() || timestamp).getTime();
  if (ms < 60000) return "just now";
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)}h`;
  return `${Math.floor(ms / 86400000)}d`;
}

const EDIT_WINDOW_MS = 15 * 60 * 1000;

function canEdit(comment, currentUserId) {
  if (comment.authorId !== currentUserId) return false;
  if (!comment.createdAt) return true;
  const created = new Date(comment.createdAt?.toDate?.() || comment.createdAt).getTime();
  return Date.now() - created < EDIT_WINDOW_MS;
}

function canDelete(comment, currentUserId, currentUserRole) {
  if (comment.authorId === currentUserId) return true;
  return meetsMinRole(currentUserRole, "companyAdmin");
}

// ---------- overflow menu ----------

const OverflowMenu = ({ comment, currentUserId, currentUserRole, onStartEdit, onDelete, readOnly }) => {
  const [open, setOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const deletingRef = useRef(false);
  const menuRef = useRef(null);
  useEffect(() => { if (readOnly) setConfirmingDelete(false); }, [readOnly]);

  useEffect(() => {
    if (!open && !confirmingDelete) return;
    const handler = (e) => {
      if (deletingRef.current) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setConfirmingDelete(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, confirmingDelete]);

  const showEdit = !readOnly && !!onStartEdit && canEdit(comment, currentUserId);
  const showDelete = !readOnly && !!onDelete && canDelete(comment, currentUserId, currentUserRole);
  const showCopy = Boolean(comment.body);
  if (!showEdit && !showDelete && !showCopy) return null;

  const handleDelete = async () => {
    if (readOnly || !onDelete || deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError(false);
    try {
      if (await onDelete(comment.id) === false) { setDeleteError(true); return; }
      setConfirmingDelete(false);
      setOpen(false);
    } catch {
      setDeleteError(true);
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  return (
    <div className="comment-card-overflow" ref={menuRef}>
      <button
        className="comment-card-overflow-btn"
        onClick={() => setOpen((p) => !p)}
        aria-label="Comment actions"
        disabled={deleting}
      >
        <MoreHorizontal size={12} />
      </button>
      {open && !confirmingDelete && (
        <div className="comment-card-overflow-menu">
          {showEdit && (
            <button onClick={() => { onStartEdit(); setOpen(false); }}>
              Edit
            </button>
          )}
          {showCopy && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(comment.body);
                setOpen(false);
              }}
            >
              Copy text
            </button>
          )}
          {showDelete && (
            <button
              className="comment-card-delete-btn"
              onClick={() => { setDeleteError(false); setConfirmingDelete(true); }}
            >
              Delete
            </button>
          )}
        </div>
      )}
      {confirmingDelete && showDelete && (
        <div className="comment-card-confirm-delete" aria-busy={deleting}>
          <span className="comment-card-confirm-label">Delete comment?</span>
          {deleteError && <div className="comment-write-error" role="alert">Couldn't delete comment. Try again.</div>}
          <div className="comment-card-confirm-actions">
            <button disabled={deleting} onClick={() => { setConfirmingDelete(false); setOpen(false); }}>Cancel</button>
            <button
              className="comment-card-confirm-delete-btn"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : deleteError ? "Retry" : "Delete"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- main component ----------

const CommentCard = ({
  comment,
  currentUserId,
  currentUserRole,
  onEdit,
  onDelete,
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(false);
  const savingRef = useRef(false);
  const [isBodyExpanded, setIsBodyExpanded] = useState(false);
  useEffect(() => { if (readOnly) setIsEditing(false); }, [readOnly]);

  const handleStartEdit = useCallback(() => {
    if (readOnly || !onEdit) return;
    setIsEditing(true);
    setEditBody(comment.body);
    setEditError(false);
  }, [comment.body, readOnly, onEdit]);

  const handleSaveEdit = useCallback(async () => {
    if (readOnly || !onEdit || savingRef.current) return;
    const trimmed = editBody.trim();
    if (!trimmed) return;
    savingRef.current = true;
    setSaving(true);
    setEditError(false);
    try {
      if (await onEdit(comment.id, trimmed) === false) { setEditError(true); return; }
      setIsEditing(false);
      setEditBody("");
    } catch {
      setEditError(true);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [comment.id, editBody, onEdit, readOnly]);

  // Soft-deleted comment
  if (comment.deleted) {
    return (
      <div className="comment-card comment-card-deleted">
        <div className="comment-card-body">This comment was deleted</div>
      </div>
    );
  }

  const initial = (comment.authorName || "?")[0].toUpperCase();

  // Edit mode
  if (isEditing && !readOnly) {
    return (
      <div className="comment-card" aria-busy={saving}>
        <textarea
          className="comment-card-edit-textarea"
          value={editBody}
          readOnly={saving}
          onChange={(e) => setEditBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); }
            if (e.key === "Escape" && !savingRef.current) setIsEditing(false);
          }}
          autoFocus
        />
        {editError && <div className="comment-write-error" role="alert">Couldn't save comment. Try again.</div>}
        <div className="comment-card-edit-actions">
          <button disabled={saving} onClick={() => setIsEditing(false)}>Cancel</button>
          <button className="comment-card-save-btn" onClick={handleSaveEdit} disabled={saving || !editBody.trim()}>
            {saving ? "Saving…" : editError ? "Retry" : "Save"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="comment-card">
      <div className="comment-card-header">
        <span className="comment-card-avatar">{initial}</span>
        <span className="comment-card-author">{comment.authorName}</span>
        <span className="comment-card-time">
          {formatTimeAgo(comment.createdAt)}
          {comment.updatedAt && " ✎"}
        </span>
        <OverflowMenu
          comment={comment}
          currentUserId={currentUserId}
          currentUserRole={currentUserRole}
          readOnly={readOnly}
          onStartEdit={onEdit ? handleStartEdit : undefined}
          onDelete={onDelete}
        />
      </div>
      {comment.anchor?.textSnippet && (
        <div className="comment-card-snippet" title={comment.anchor.textSnippet}>
          &ldquo;{comment.anchor.textSnippet}&rdquo;
        </div>
      )}
      {comment.rowAnchor?.rowLabel && (
        <div className="comment-card-snippet" title={`Row: ${comment.rowAnchor.rowLabel}`}>
          Row: {comment.rowAnchor.rowLabel}
        </div>
      )}
      <div className="comment-card-body">
        {comment.body.length > BODY_TRUNCATE_LENGTH && !isBodyExpanded
          ? comment.body.slice(0, BODY_TRUNCATE_LENGTH) + "…"
          : comment.body}
      </div>
      {comment.body.length > BODY_TRUNCATE_LENGTH && (
        <button
          className="comment-card-body-toggle"
          onClick={() => setIsBodyExpanded((p) => !p)}
        >
          {isBodyExpanded ? (
            <>Show less <ChevronUp size={10} /></>
          ) : (
            <>Show more <ChevronDown size={10} /></>
          )}
        </button>
      )}
    </div>
  );
};

export default React.memo(CommentCard);
